import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { Form, Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation, withTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { BiPencil } from "react-icons/bi";
import { BsArrowReturnRight, BsFileEarmarkFill, BsX, BsBuildings, BsThreeDotsVertical, BsPencil, BsPaperclip } from "react-icons/bs";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FcFinePrint } from "react-icons/fc";
import { toast } from "react-toastify";
import { LoaderContext, RegulationContext } from "../../../../App";
import StandardService from "../../../../core/service/StandardService";
import '../DataView.scss';
import ConfirmationModal from "../../../../components/ConfirmationModal/ConfirmationModal";
import Utils from "../../../../core/utilities/Utils";
import { CompareCategoryType, LS_DV_FILTER_KEY } from "../../../../core/constants/common";
import './QualitativeDataCardView.scss';
import assessment_pic from "../../../../images/Assessment.png";
import commentAttached from "../../../../images/comment_attach.png";

const QualitativeDataCardView = ({ t, filterData, view, viewType, columnHideShow, businessUnits = [] }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { glbRegulation } = useContext(RegulationContext);
    const glbRegulationId = glbRegulation?.id ?? "";
    const { setLoaderDisplay } = useContext(LoaderContext);

    // Get filter data from props, location state, or localStorage
    const filterFromState = location.state?.filter;
    const dvFilter = JSON.parse(localStorage.getItem(LS_DV_FILTER_KEY));
    const initialFilter = filterData || filterFromState || dvFilter || {};

    const [filter, setFilter] = useState(initialFilter);
    const [qualitativeData, setQualitativeData] = useState([]);
    const [qnAnsData, setQnAnsData] = useState({});
    const [topicVisibility, setTopicVisibility] = useState({});
    const [branchVisibility, setBranchVisibility] = useState({});
    const [metricVisibility, setMetricVisibility] = useState({});
    const [qnVisibility, setQnVisibility] = useState({});
    const [editedAnswers, setEditedAnswers] = useState({}); // Track which answers are being edited
    const [tempAnswers, setTempAnswers] = useState({}); // Store temporary answer values while editing
    const [subQnVisibility, setSubQnVisibility] = useState({});
    const [subQnAnsData, setSubQnAnsData] = useState({});
    const [branchesFromFilteredData, setBranchesFromFilteredData] = useState([]);
    const [isEdit, setIsEdit] = useState(view == 'EDIT_VIEW' ? true : false);

    const [selectedQnCom, setSelectedQnCom] = useState({});
    const [showCommentsModal, setShowCommentsModal] = useState(false);
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [qnComments, setQnComments] = useState('');
    const [fileUploader, setFileUploader] = useState('');
    const [selectedFile, setSelectedFile] = useState({});
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [messageConfirmation, setMessageConfirmation] = useState('');
    const [isCommentEdit, setIsCommentEdit] = useState(false);
    const [tempComments, setTempComments] = useState('');
    const [tempAttachments, setTempAttachments] = useState([]);
    const fileInputRef = useRef();
    const [colHideShow, setColHideShow] = useState({
        codes: true,
        uom: true
    });
    const { k, i18n } = useTranslation();

    const prevFilterDataRef = useRef(null);

    useEffect(() => {
        if (!glbRegulationId) return;
        if (!filterData) return;

        // Prevent duplicate API calls by checking if filterData actually changed
        const filterDataString = JSON.stringify(filterData);
        if (prevFilterDataRef.current === filterDataString) {
            return;
        }
        prevFilterDataRef.current = filterDataString;

        setFilter(filterData);
        if (filterData && Object.keys(filterData).length > 0) {
            getQualitativeData(filterData);
        }
    }, [glbRegulationId, filterData]);

    useEffect(() => {
        setIsEdit(view == 'EDIT_VIEW' ? true : false);
        setIsCommentEdit(view == 'EDIT_VIEW' ? true : false);
    }, [view]);

    useEffect(() => {
        if (columnHideShow) {
            setColHideShow(columnHideShow);
        }
    }, [columnHideShow]);

    useEffect(() => {
        let branches = [];
        let order = filter.businessUnitIds || [];
        for (let index = 0; index < qualitativeData?.length; index++) {
            const qData = qualitativeData[index];
            if (qData?.metricConsolidated?.length > 0) {
                branches = qData?.metricConsolidated[0].branchConsolidated;
                break;
            }
        }
        branches.sort((a, b) => {
            return order.indexOf(a.branchId) - order.indexOf(b.branchId);
        });
        setBranchesFromFilteredData(branches);
    }, [qualitativeData]);

    const getQualitativeData = (f) => {
        let order = f.businessUnitIds || [];
        setLoaderDisplay(true);
        StandardService.getQualitativeDataByFilter(glbRegulationId, f.reportingPeriod, f)
            .then(res => {
                setLoaderDisplay(false);
                if (res.status !== 200) return;
                let result = res.data.data.topics ? res.data.data.topics : [];
                if (result.length > 0) {
                    result.forEach(element => {
                        if (element.metricConsolidated.length > 0) {
                            element.metricConsolidated.forEach(metrics => {
                                metrics['branchConsolidated'].sort((a, b) => {
                                    return order.indexOf(a.branchId) - order.indexOf(b.branchId);
                                });
                            })
                        }
                    });
                }
                const transformedData = result.map((obj) => transformObject(obj));
                setQualitativeData(transformedData);
                setQnAnsData({});
                setQnVisibility({});
                setSubQnVisibility({});
                setSubQnAnsData({});
            })
            .catch(err => {
                setLoaderDisplay(false);
                console.error('Error fetching qualitative data:', err);
                toast.error(t("QualitativeDataCardView.error.fetchData.label"));
            });
    };

    const transformObject = (obj) => {
        const transformedObj = { ...obj };
        // Add the translated values to the main object based on languageCode
        if (obj.localizedContents && obj.localizedContents.length > 0) {
            obj.localizedContents.forEach((item) => {
                if (item.fieldName === 'name') {
                    transformedObj[item.languageCode] = item.translatedValue;
                }
            });
        }
        // Recursively process the children objects
        if (obj.metricConsolidated && obj.metricConsolidated.length > 0) {
            transformedObj.metricConsolidated = obj.metricConsolidated.map((child) => transformObject(child));
        }
        return transformedObj;
    };

    const showOrHideBranch = (branchId) => {
        const vis = { ...branchVisibility };
        vis[branchId] = vis[branchId] === 'hide' ? 'show' : 'hide';
        setBranchVisibility(vis);
    };

    const showOrHideTopic = (topicId, branchId) => {
        const key = `${branchId}-${topicId}`;
        const vis = { ...topicVisibility };
        vis[key] = vis[key] === 'hide' ? 'show' : 'hide';
        setTopicVisibility(vis);
    };

    const showOrHideMetric = (metricId, topicId, branchId) => {
        const key = `${branchId}-${topicId}-${metricId}`;
        const currentMetricState = metricVisibility[key];
        const willShowMetric = currentMetricState === 'hide' || currentMetricState === undefined;

        const vis = { ...metricVisibility };
        vis[key] = willShowMetric ? 'show' : 'hide';
        setMetricVisibility(vis);

        // Use composite key for question visibility (branchId-metricId) to avoid conflicts across branches
        const qnKey = `${branchId}-${metricId}`;

        // If showing the metric, fetch questions for this metric and set visibility
        if (willShowMetric) {
            // Set question visibility to 'show' immediately
            const qnVis = { ...qnVisibility };
            qnVis[qnKey] = 'show';
            setQnVisibility(qnVis);

            // Always fetch questions when showing metric (even if they might exist)
            // This ensures fresh data on first click
            getQuestionAnswers(metricId, filter, branchId);
        } else {
            // If hiding, clear the questions data for this metric
            const qnVis = { ...qnVisibility };
            qnVis[qnKey] = 'hide';
            setQnVisibility(qnVis);
            setQnAnsData(existingQns => {
                const updated = { ...existingQns };
                updated[qnKey] = [];
                return updated;
            });
        }
    };

    const showOrHideQn = (qnId) => {
        const vis = { ...qnVisibility };
        vis[qnId] = vis[qnId] === 'hide' ? 'show' : 'hide';
        setQnVisibility(vis);
    };

    const showOrHideSubQn = (subQnId) => {
        const vis = { ...subQnVisibility };
        vis[subQnId] = vis[subQnId] === 'hide' ? 'show' : 'hide';
        setSubQnVisibility(vis);
    };

    const handleSubQnAnswerChange = (e, subQnId, branchId) => {
        const value = e.target.value;
        const subQnAns = { ...subQnAnsData };
        if (!subQnAns[subQnId]) {
            subQnAns[subQnId] = {};
        }
        subQnAns[subQnId][branchId] = value;
        setSubQnAnsData(subQnAns);
    };

    const getRemarkAttachmentsFields = (ans) => {
        const fields = {};
        if (ans?.remark) {
            fields.remark = ans.remark;
        }
        if (ans?.attachments?.length > 0) {
            fields.attachments = ans.attachments.map(a => ({ id: a.id }));
        }
        return fields;
    };

    const saveAnswer = (qnId, branchId, question) => {
        const answerValue = qnAnsData[qnId]?.[branchId] || '';
        if (!answerValue.trim()) {
            toast.warning(t("QualitativeDataCardView.warning.emptyAnswer.label"));
            return;
        }

        const branch = branchesFromFilteredData.find(b => b.branchId === branchId);
        const ans = branch?.answers?.length > 0 ? branch.answers[0] : {};
        const payload = [{
            id: ans.id > 0 ? ans.id : null,
            questionId: question.questionId || qnId,
            answer: answerValue,
            branchId: branchId,
            ...getRemarkAttachmentsFields(ans)
        }];

        setLoaderDisplay(true);
        StandardService.postQualMetricAnswers(filter?.reportingPeriod, payload)
            .then(res => {
                setLoaderDisplay(false);
                if (res.status === 200) {
                    toast.success(t("QualitativeDataCardView.success.answerSaved.label"));
                    getQualitativeData(filter);
                }
            })
            .catch(err => {
                setLoaderDisplay(false);
                console.error('Error saving answer:', err);
                toast.error(t("QualitativeDataCardView.error.saveAnswer.label"));
            });
    };

    const saveSubQnAnswer = (subQnId, branchId, subQn) => {
        const answerValue = subQnAnsData[subQnId]?.[branchId] || '';
        if (!answerValue.trim()) {
            toast.warning(t("QualitativeDataCardView.warning.emptyAnswer.label"));
            return;
        }

        const branch = branchesFromFilteredData.find(b => b.branchId === branchId);
        const ans = branch?.answers?.length > 0 ? branch.answers[0] : {};
        const payload = [{
            id: ans.id > 0 ? ans.id : null,
            questionId: subQn.questionId || subQnId,
            answer: answerValue,
            branchId: branchId,
            ...getRemarkAttachmentsFields(ans)
        }];

        setLoaderDisplay(true);
        StandardService.postQualMetricAnswers(filter?.reportingPeriod, payload)
            .then(res => {
                setLoaderDisplay(false);
                if (res.status === 200) {
                    toast.success(t("QualitativeDataCardView.success.answerSaved.label"));
                    getQualitativeData(filter);
                }
            })
            .catch(err => {
                setLoaderDisplay(false);
                console.error('Error saving sub-question answer:', err);
                toast.error(t("QualitativeDataCardView.error.saveAnswer.label"));
            });
    };

    const onEditAnswer = (questionId, branchId, branch) => {
        const key = `${questionId}-${branchId}`;
        const currentAnswer = branch?.answers?.length > 0 ? branch.answers[0].answer : '';

        console.log('onEditAnswer called:', { questionId, branchId, key, currentAnswer, isEdit });

        setEditedAnswers(prev => ({
            ...prev,
            [key]: true
        }));

        setTempAnswers(prev => ({
            ...prev,
            [key]: currentAnswer
        }));
    };

    const cancelAnswerEdit = (questionId, branchId) => {
        const key = `${questionId}-${branchId}`;
        setEditedAnswers(prev => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
        });
        setTempAnswers(prev => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
        });
    };

    const handleTempAnswerChange = (questionId, branchId, value) => {
        const key = `${questionId}-${branchId}`;
        setTempAnswers(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const saveIndividualAnswer = (questionId, branchId, branch, metricId) => {
        const key = `${questionId}-${branchId}`;
        const answerValueRaw = tempAnswers[key] || '';
        const answerValue = answerValueRaw.trim();

        if (!answerValue) {
            toast.warning(t("QualitativeDataCardView.warning.emptyAnswer.label"));
            return;
        }

        const ans = branch?.answers?.length > 0 ? branch.answers[0] : {};
        const payload = [{
            id: ans.id > 0 ? ans.id : null,
            questionId: questionId,
            answer: answerValue,
            branchId: branchId,
            ...getRemarkAttachmentsFields(ans)
        }];

        setLoaderDisplay(true);
        StandardService.postQualMetricAnswers(filter?.reportingPeriod, payload)
            .then(res => {
                setLoaderDisplay(false);
                if (res.status === 200) {
                    // Refresh question/answer data from API so latest content (and timestamps) are displayed
                    if (metricId) {
                        getQuestionAnswers(metricId, filter, branchId);
                    }
                    // Show success toast and exit edit mode so answer-container hides and answer-display is shown
                    toast.success(t("QualitativeDataCardView.success.answerSaved.label"));
                    cancelAnswerEdit(questionId, branchId);
                }
            })
            .catch(err => {
                setLoaderDisplay(false);
                console.error('Error saving answer:', err);
                toast.error(t("QualitativeDataCardView.error.saveAnswer.label"));
            });
    };

    const renderAnswerBox = (question, branchData, branchId, metricId) => {
        const key = `${question.questionId}-${branchId}`;
        const answerValue = tempAnswers[key] ?? (branchData?.answers?.length > 0 ? branchData.answers[0].answer : '');

        console.log('renderAnswerBox called:', { questionId: question.questionId, branchId, key, answerValue, isEdit });

        return (
            <div className="answer-container">
                <div>
                    <textarea
                        rows={6}
                        className="form-control editable"
                        value={answerValue}
                        disabled={false}
                        onChange={(e) => handleTempAnswerChange(question.questionId, branchId, e.target.value)}
                        placeholder={t('EnterYourAnswerHere.label')}
                    />
                </div>
                <div className="p-2">
                    <button
                        type="button"
                        className="btn btn-primary me-2"
                        onClick={() => saveIndividualAnswer(question.questionId, branchId, branchData, metricId)}
                    >
                        {t("Save.label")}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => cancelAnswerEdit(question.questionId, branchId)}
                    >
                        {t("Close.label")}
                    </button>
                </div>
            </div>
        );
    };

    const hasCommentsOrAttachments = (branch) => {
        const ans = branch?.answers?.length > 0 ? branch.answers[0] : null;
        if (!ans) return false;
        return !!ans.remark || (ans.attachments && ans.attachments.length > 0);
    };

    const showCommentsAndUploads = (question, branch, metricId) => {
        setShowCommentsModal(true);
        let data = {
            questionId: question.questionId,
            metricId: metricId,
            dataPointId: branch.dataPointId || null,
            branchId: branch.branchId,
            id: branch.answers?.length > 0 ? branch.answers[0].id : null,
            remark: branch.answers?.length > 0 ? branch.answers[0].remark : '',
            attachments: branch.answers?.length > 0 ? branch.answers[0].attachments : [],
            answer: branch.answers?.length > 0 ? branch.answers[0].answer : ''
        };
        setQnComments(branch.answers?.length > 0 ? branch.answers[0].remark : '');
        setAttachedFiles(branch.answers?.length > 0 ? branch.answers[0].attachments : []);
        setSelectedQnCom(data);
        setIsCommentEdit(false);
    };

    const onEditComments = () => {
        setTempComments(qnComments);
        setTempAttachments(attachedFiles);
        setIsCommentEdit(true);
    };

    const handleCommentsModalClose = () => {
        setShowCommentsModal(false);
        setSelectedQnCom({});
        setQnComments('');
        setAttachedFiles([]);
        setTempComments('');
        setTempAttachments([]);
    };


    const uploadAttachments = (fileUploaded) => {
        if (fileUploaded) {
            const formData = new FormData();
            formData.append("document", fileUploaded);
            setLoaderDisplay(true);
            StandardService.uploadAttachments(formData).then(res => {
                setLoaderDisplay(false);
                if (res.status === 200 || res.status === 201) {
                    let files = tempAttachments;
                    files.push(res.data);
                    setTempAttachments(files);
                    toast.success(t("QualitativeDataCardView.success.fileUploaded.label"));
                }
            }).catch(err => {
                setLoaderDisplay(false);
                console.error('Error uploading file:', err);
                toast.error(t("QualitativeDataCardView.error.uploadFile.label"));
            });
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        files.forEach(file => {
            uploadAttachments(file);
        });
    };

    const saveCommentsAndFiles = () => {
        let existData = selectedQnCom;
        existData['remark'] = tempComments || qnComments;
        existData['attachments'] = tempAttachments;
        setSelectedQnCom(existData);

        const payload = {
            questionId: existData.questionId,
            branchId: existData.branchId,
            answer: existData.answer || '',
            remark: existData.remark,
            attachments: (existData.attachments || []).map(a => ({ id: a.id })),
            standardId: glbRegulationId
        };

        setLoaderDisplay(true);
        StandardService.saveBranchCommentsAttachments(filter.reportingPeriod, payload).then(res => {
            setLoaderDisplay(false);
            if (res.status === 200) {
                toast.success(t("QualitativeDataCardView.success.commentsSaved.label"));
                if (existData.metricId) {
                    getQuestionAnswers(existData.metricId, filter, existData.branchId);
                }
                handleCommentsModalClose();
            }
        }).catch((error) => {
            setLoaderDisplay(false);
            console.error('Error saving comments and files:', error);
            toast.error(t("QualitativeDataCardView.error.saveComments.label"));
        });
    };

    const showConfirm = (file) => {
        setSelectedFile(file);
        setMessageConfirmation(t("QualitativeDataCardView.confirmation.deleteFile.label"));
        setShowConfirmation(true);
    };

    const downloadAttchement = (data) => {
        if (data.externalId) {
            setLoaderDisplay(true);
            StandardService.getDocumentsAttached(data.externalId)
                .then((response) => {
                    setLoaderDisplay(false);
                    let fetchDataModified = `data:${response.data.mimeType};base64,${response.data.file}`;
                    let link = document.createElement("a");
                    link.href = fetchDataModified;
                    link.download = response.data.name;
                    link.click();
                })
                .catch((error) => {
                    setLoaderDisplay(false);
                    console.log(error);
                });
        } else {
            downloadAttchementAlternative(data.id);
        }
    };

    const downloadAttchementAlternative = (id) => {
        setLoaderDisplay(true);
        StandardService.getDocumentsAttachedAlt(id)
            .then((response) => {
                setLoaderDisplay(false);
                let fetchDataModified = `data:${response.data.mimeType};base64,${response.data.file}`;
                let link = document.createElement("a");
                link.href = fetchDataModified;
                link.download = response.data.name;
                link.click();
            })
            .catch((error) => {
                setLoaderDisplay(false);
                console.log(error);
            });
    };

    const deleteFile = () => {
        const index = tempAttachments.findIndex(x => x.id === selectedFile.id);
        if (index >= 0) {
            tempAttachments.splice(index, 1);
            let files = JSON.stringify(tempAttachments);
            setTempAttachments(JSON.parse(files));
            toast.success(t("QualitativeDataCardView.success.fileRemoved.label"));
        }
        setShowConfirmation(false);
        setSelectedFile({});
    };

    // Memoize filtered business units and parent lookup map for performance
    const { parentBusinessUnits, childBusinessUnits, parentNameMap } = useMemo(() => {
        const parents = businessUnits.filter(bu => bu.level === 1);
        const children = businessUnits.filter(bu => bu.level === 2);

        // Create a map for O(1) lookup: branchId -> parentName
        const parentMap = {};
        children.forEach(child => {
            const parentId = child.parentId || child.parentBusinessUnitId || child.parent?.id;
            if (parentId) {
                const parent = parents.find(p => p.id === parentId);
                if (parent) {
                    parentMap[child.id] = parent.name;
                }
            }
        });

        return {
            parentBusinessUnits: parents,
            childBusinessUnits: children,
            parentNameMap: parentMap
        };
    }, [businessUnits]);

    // Reorganize data by branch -> topic -> metric structure
    const getParentNameForBranch = (branchId) => {
        // Use the pre-computed map for O(1) lookup
        return parentNameMap[branchId] || null;
    };

    const getBranchDisplayName = (branch) => {
        const parentName = getParentNameForBranch(branch.branchId);
        if (parentName) {
            return `${branch.branchName} (${parentName})`;
        }
        return branch.branchName;
    };

    const organizeDataByBranch = () => {
        if (!qualitativeData || qualitativeData.length === 0 || !branchesFromFilteredData || branchesFromFilteredData.length === 0) {
            return {};
        }

        const organizedData = {};

        // Initialize branches
        branchesFromFilteredData.forEach(branch => {
            organizedData[branch.branchId] = {
                branchId: branch.branchId,
                branchName: branch.branchName,
                topics: {}
            };
        });

        // Organize topics and metrics by branch
        qualitativeData.forEach(topic => {
            if (topic.metricConsolidated && topic.metricConsolidated.length > 0) {
                topic.metricConsolidated.forEach(metric => {
                    if (metric.branchConsolidated && metric.branchConsolidated.length > 0) {
                        metric.branchConsolidated.forEach(branchData => {
                            const branchId = branchData.branchId;
                            if (organizedData[branchId]) {
                                if (!organizedData[branchId].topics[topic.topicId]) {
                                    organizedData[branchId].topics[topic.topicId] = {
                                        topicId: topic.topicId,
                                        topicName: topic.topicName || topic.name,
                                        localizedContents: topic.localizedContents,
                                        metrics: []
                                    };
                                }
                                organizedData[branchId].topics[topic.topicId].metrics.push({
                                    ...metric,
                                    branchData: branchData
                                });
                            }
                        });
                    }
                });
            }
        });

        return organizedData;
    };

    const renderQualitativeContent = () => {
        if (!qualitativeData || qualitativeData.length === 0) {
            return (
                <div className="text-center p-5">
                    <div className="Nodatafound_box">
                        <div className="assessment_pic">
                            <img src={assessment_pic} alt="Assessment" />
                        </div>
                        <h6>{t("Nodatafound.label")}</h6>
                    </div>
                </div>
            );
        }

        // Check if branchConsolidated is empty across all metrics
        const hasBranchConsolidated = qualitativeData.some(topic =>
            topic.metricConsolidated && topic.metricConsolidated.some(metric =>
                metric.branchConsolidated && metric.branchConsolidated.length > 0
            )
        );

        if (!hasBranchConsolidated || !branchesFromFilteredData || branchesFromFilteredData.length === 0) {
            return (
                <div className="text-center p-5">
                    <div className="Nodatafound_box" >
                        <div className="assessment_pic" style={{ width: '96px', height: '96px', margin: '15px auto' }}>
                            <img src={assessment_pic} alt="Assessment" />
                        </div>
                        <h6>{t("PleaseSelectEntityFromFilter.label")}</h6>
                    </div>
                </div>
            );
        }

        const organizedData = organizeDataByBranch();
        const categoryName = Utils.toTitleCase(CompareCategoryType[filter.categoryType] || 'ENVIRONMENTAL');

        return (
            <div className="qualitative-card-view-container mt-3">
                {/* Category Header */}
                <div className="qualitative-category-card mb-4 d-none">
                    <div className="qualitative-category-header">
                        <h5 className="mb-0 text-primary">
                            {categoryName}
                        </h5>
                    </div>
                </div>

                {/* Branch Cards */}
                {branchesFromFilteredData.map((branch) => {
                    const branchData = organizedData[branch.branchId];
                    if (!branchData || !branchData.topics || Object.keys(branchData.topics).length === 0) {
                        return null;
                    }

                    const isBranchVisible = branchVisibility[branch.branchId] !== 'hide';

                    return (
                        <div key={branch.branchId} className="qualitative-branch-card">
                            <div
                                className="qualitative-branch-header"
                                onClick={() => showOrHideBranch(branch.branchId)}
                            >
                                <div className="d-flex align-items-center">
                                    <span className="branch-icon me-2">
                                        {isBranchVisible ? <FaChevronUp /> : <FaChevronDown />}
                                    </span>
                                    <BsBuildings className="me-2 text-primary" />
                                    <h5 className="mb-0">{getBranchDisplayName(branch)}</h5>
                                </div>
                            </div>
                            <div className="qualitative-topic-card">
                                {isBranchVisible && Object.values(branchData.topics).map((topic) => {
                                    const topicKey = `${branch.branchId}-${topic.topicId}`;
                                    const isTopicVisible = topicVisibility[topicKey] !== 'hide';
                                    const topicDisplayName = (i18n.language && topic.localizedContents)
                                        ? topic.localizedContents.find(lc => lc.languageCode === i18n.language && lc.fieldName === 'name')?.translatedValue || topic.topicName
                                        : topic.topicName;

                                    return (

                                        <div key={topic.topicId} className="qualitative-topic-cardInner mb-2">
                                            <div
                                                className="qualitative-topic-header"
                                                onClick={() => showOrHideTopic(topic.topicId, branch.branchId)}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <span className="topic-icon me-2">
                                                        {isTopicVisible ? <FaChevronUp /> : <FaChevronDown />}
                                                    </span>
                                                    <h6 className="mb-0">{topicDisplayName}</h6>
                                                </div>
                                            </div>
                                            <div className="qualitative-metric-card">
                                                {isTopicVisible && topic.metrics.map((metric, metricIndex) => {
                                                    const metricKey = `${branch.branchId}-${topic.topicId}-${metric.metricId}`;
                                                    // Initially show arrow down (undefined or 'hide' means collapsed)
                                                    const isMetricVisible = metricVisibility[metricKey] === 'show';
                                                    const metricDisplayName = (i18n.language && metric.localizedContents)
                                                        ? metric.localizedContents.find(lc => lc.languageCode === i18n.language && lc.fieldName === 'name')?.translatedValue || metric.metricName
                                                        : metric.metricName;

                                                    return (

                                                        <div key={metric.metricId} className="qualitative-metric-cardInner">
                                                            <div className="qualitative-metric-header"
                                                                onClick={() => showOrHideMetric(metric.metricId, topic.topicId, branch.branchId)}
                                                            >
                                                                <span className="metric-icon me-2">
                                                                    {isMetricVisible ? <FaChevronUp /> : <FaChevronDown />}
                                                                </span>
                                                                <span className="metric-name">
                                                                    {metricDisplayName}
                                                                </span>
                                                                {colHideShow?.codes && (
                                                                    <span className="metric-code ms-2 text-muted">
                                                                        ({metric.metricCode})
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {isMetricVisible && (
                                                                <>
                                                                    {metric.questionCount > 0 && (
                                                                        <div className="metric-questions-section">
                                                                            {(() => {
                                                                                const qnKey = `${branch.branchId}-${metric.metricId}`;
                                                                                const isVisible = qnVisibility[qnKey] === 'show';
                                                                                const hasData = qnAnsData[qnKey] && qnAnsData[qnKey].length > 0;

                                                                                if (!isVisible || !hasData) return null;

                                                                                return (
                                                                                    <div className="questions-list mt-2">
                                                                                        {qnAnsData[qnKey].map((qn, qnIndex) => {
                                                                                            const branchQnData = qn.branchConsolidated?.find(b => b.branchId === branch.branchId);
                                                                                            return (
                                                                                                <div key={qn.questionId} className="question-item mb-2 p-2">
                                                                                                    <div className="question-text mb-2">
                                                                                                        {/* {qn.questionCode && (
                                                                                                <span className="question-code me-2 text-muted">({qn.questionCode})</span>
                                                                                                )} */}
                                                                                                        <strong>{qn.questionText || (i18n.language && qn[i18n.language]) || 'No question text'}</strong>
                                                                                                    </div>
                                                                                                    {branchQnData && (
                                                                                                        <div className="question-answer">
                                                                                                            {(() => {
                                                                                                                const editKey = `${qn.questionId}-${branch.branchId}`;
                                                                                                                const isEditing = editedAnswers[editKey];
                                                                                                                return isEditing;
                                                                                                            })() ? (
                                                                                                                renderAnswerBox(qn, branchQnData, branch.branchId, metric.metricId)
                                                                                                            ) : (
                                                                                                                <>
                                                                                                                    <div
                                                                                                                        className="answer-display cursor-pointer"
                                                                                                                        onClick={(e) => {
                                                                                                                            e.stopPropagation();
                                                                                                                            // Always allow editing when clicking on answer (whether it exists or not)
                                                                                                                            onEditAnswer(qn.questionId, branch.branchId, branchQnData);
                                                                                                                        }}
                                                                                                                    >
                                                                                                                        {branchQnData.answers?.length > 0 && branchQnData.answers[0].answer && branchQnData.answers[0].answer.trim()
                                                                                                                            ? (branchQnData.answers[0].answer.length > 100
                                                                                                                                ? branchQnData.answers[0].answer.substring(0, 100) + '...'
                                                                                                                                : branchQnData.answers[0].answer)
                                                                                                                            : (
                                                                                                                                // Only show "Enter your answer here" for main question if there are NO subQuestions
                                                                                                                                !qn.subQuestions || qn.subQuestions.length === 0
                                                                                                                                    ? <div
                                                                                                                                        className="EnterYourAnswerHere_label cursor-pointer"
                                                                                                                                        onClick={(e) => {
                                                                                                                                            e.stopPropagation();
                                                                                                                                            e.preventDefault();
                                                                                                                                            // Always allow editing when clicking "Enter your answer here"
                                                                                                                                            onEditAnswer(qn.questionId, branch.branchId, branchQnData);
                                                                                                                                        }}
                                                                                                                                    >
                                                                                                                                        {t('EnterYourAnswerHere.label')}
                                                                                                                                    </div>
                                                                                                                                    : null
                                                                                                                            )}
                                                                                                                    </div>
                                                                                                                    {hasCommentsOrAttachments(branchQnData) && (
                                                                                                                        <>
                                                                                                                            {branchQnData.answers?.[0]?.attachments?.length > 0 && (
                                                                                                                                <OverlayTrigger placement="top" overlay={<Tooltip>{t('AttachmentsAdded.label')}</Tooltip>}>
                                                                                                                                    <span className="attachClip">
                                                                                                                                        <BsPaperclip size={14} />
                                                                                                                                    </span>
                                                                                                                                </OverlayTrigger>
                                                                                                                            )}
                                                                                                                            <OverlayTrigger
                                                                                                                                placement="auto"
                                                                                                                                overlay={<Tooltip>{t("CommentsandDocumentsFile.label")}</Tooltip>}
                                                                                                                            >
                                                                                                                                <span
                                                                                                                                    className="commAtt cursor-pointer"
                                                                                                                                    onClick={(e) => {
                                                                                                                                        e.stopPropagation();
                                                                                                                                        showCommentsAndUploads(qn, branchQnData, metric.metricId);
                                                                                                                                    }}
                                                                                                                                >
                                                                                                                                    <BsThreeDotsVertical />
                                                                                                                                </span>
                                                                                                                            </OverlayTrigger>
                                                                                                                        </>
                                                                                                                    )}
                                                                                                                </>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    )}
                                                                                                    {qn.subQuestions && qn.subQuestions.length > 0 && (
                                                                                                        <div className="subquestions-list ms-3 mt-2">
                                                                                                            {qn.subQuestions.map((subQn, subIndex) => {
                                                                                                                const branchSubQnData = subQn.branchConsolidated?.find(b => b.branchId === branch.branchId);
                                                                                                                return (
                                                                                                                    <div key={subQn.questionId} className="subquestion-item">
                                                                                                                        <div className="subquestion-item-inner">
                                                                                                                            <div className="subquestion-text mb-2">
                                                                                                                                <strong>{subQn.questionText || (i18n.language && subQn[i18n.language]) || 'No question text'}</strong>
                                                                                                                            </div>
                                                                                                                            {branchSubQnData && (
                                                                                                                                <div className="subquestion-answer">
                                                                                                                                    {(() => {
                                                                                                                                        const editKey = `${subQn.questionId}-${branch.branchId}`;
                                                                                                                                        const isEditing = editedAnswers[editKey];
                                                                                                                                        return isEditing;
                                                                                                                                    })() ? (
                                                                                                                                        renderAnswerBox(subQn, branchSubQnData, branch.branchId, metric.metricId)
                                                                                                                                    ) : (
                                                                                                                                        <>
                                                                                                                                            <div
                                                                                                                                                className="answer-display cursor-pointer"
                                                                                                                                                onClick={(e) => {
                                                                                                                                                    e.stopPropagation();
                                                                                                                                                    // Always allow editing when clicking on answer (whether it exists or not)
                                                                                                                                                    onEditAnswer(subQn.questionId, branch.branchId, branchSubQnData);
                                                                                                                                                }}
                                                                                                                                            >
                                                                                                                                                {branchSubQnData.answers?.length > 0 && branchSubQnData.answers[0].answer && branchSubQnData.answers[0].answer.trim()
                                                                                                                                                    ? (branchSubQnData.answers[0].answer.length > 100
                                                                                                                                                        ? branchSubQnData.answers[0].answer.substring(0, 100) + '...'
                                                                                                                                                        : branchSubQnData.answers[0].answer)
                                                                                                                                                    : <div
                                                                                                                                                        className="EnterYourAnswerHere_label cursor-pointer"
                                                                                                                                                        onClick={(e) => {
                                                                                                                                                            e.stopPropagation();
                                                                                                                                                            e.preventDefault();
                                                                                                                                                            // Always allow editing when clicking "Enter your answer here"
                                                                                                                                                            onEditAnswer(subQn.questionId, branch.branchId, branchSubQnData);
                                                                                                                                                        }}
                                                                                                                                                    >
                                                                                                                                                        {t('EnterYourAnswerHere.label')}
                                                                                                                                                    </div>}
                                                                                                                                            </div>

                                                                                                                                            <OverlayTrigger
                                                                                                                                                placement="auto"
                                                                                                                                                overlay={<Tooltip>{t("CommentsandDocumentsFile1.label")}</Tooltip>}
                                                                                                                                            >
                                                                                                                                                <span
                                                                                                                                                    className="commentAttach_btn cursor-pointer"
                                                                                                                                                    onClick={(e) => {
                                                                                                                                                        e.stopPropagation();
                                                                                                                                                        showCommentsAndUploads(subQn, branchSubQnData, metric.metricId);
                                                                                                                                                    }}
                                                                                                                                                >
                                                                                                                                                    {branchSubQnData.answers?.[0]?.attachments?.length > 0 && (
                                                                                                                                                        <span className="attachmentsFile_circle">
                                                                                                                                                        </span>
                                                                                                                                                    )}
                                                                                                                                                    <img src={commentAttached} />
                                                                                                                                                </span>
                                                                                                                                            </OverlayTrigger>
                                                                                                                                        </>
                                                                                                                                    )}
                                                                                                                                </div>
                                                                                                                            )}
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                );
                                                                                                            })}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                );
                                                                            })()}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>

                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                        </div>
                    );
                })}
            </div>
        );
    };

    const showQnAnsForMetric = (metricId, branchId) => {
        // Use composite key for question visibility (branchId-metricId) to avoid conflicts across branches
        const qnKey = `${branchId}-${metricId}`;

        // Check current state before toggling
        const currentState = qnVisibility[qnKey];
        const willBeShown = currentState !== 'show';

        // Toggle visibility
        const vis = { ...qnVisibility };
        vis[qnKey] = willBeShown ? 'show' : 'hide';
        setQnVisibility(vis);

        // If showing, fetch questions immediately
        if (willBeShown) {
            // Check if questions are already loaded
            if (!qnAnsData[qnKey] || qnAnsData[qnKey].length === 0) {
                getQuestionAnswers(metricId, filter, branchId);
            }
        } else {
            // If hiding, clear the questions data for this metric
            setQnAnsData(existingQns => {
                const updated = { ...existingQns };
                updated[qnKey] = [];
                return updated;
            });
        }
    };

    const getQuestionAnswers = (metricId, f, branchId = null) => {
        let order = f.businessUnitIds || [];

        // If branchId is provided, create a filter with only that branch
        const filterForApi = { ...f };
        if (branchId) {
            filterForApi.businessUnitIds = [branchId];
        }

        setLoaderDisplay(true);
        StandardService.getQuestionAnswers(metricId, f.reportingPeriod, filterForApi)
            .then((res) => {
                setLoaderDisplay(false);
                if (res.status !== 200) return;
                let result = res.data.data.questions;
                if (result.length > 0) {
                    result.forEach(element => {
                        if (element.branchConsolidated) {
                            element['branchConsolidated'].sort((a, b) => {
                                return order.indexOf(a.branchId) - order.indexOf(b.branchId);
                            });
                        }
                        if (element.subQuestions && element.subQuestions.length > 0) {
                            element.subQuestions.forEach(subelement => {
                                if (subelement.branchConsolidated) {
                                    subelement['branchConsolidated'].sort((a, b) => {
                                        return order.indexOf(a.branchId) - order.indexOf(b.branchId);
                                    });
                                }
                            });
                        }
                    });
                }
                // Use composite key for question data (branchId-metricId) to avoid conflicts across branches
                const qnKey = branchId ? `${branchId}-${metricId}` : metricId;

                setQnAnsData(existingQns => {
                    const transformedQuestions = result.map(transformQuestionObject);
                    existingQns[qnKey] = transformedQuestions.length > 0 ? transformedQuestions : result;
                    return { ...existingQns };
                });

                // Set visibility to 'show' after loading questions (matching QualitativeDataView.js)
                const vis = { ...qnVisibility };
                vis[qnKey] = 'show';
                setQnVisibility(vis);
            })
            .catch((e) => {
                setLoaderDisplay(false);
                console.error('Error fetching question answers:', e);
            });
    };

    const transformQuestionObject = (question) => {
        const transformed = { ...question };

        // Extract questionText from localizedContents (fieldName === 'question')
        // This matches QualitativeDataView.js pattern exactly
        if (question.localizedContents?.length > 0) {
            question.localizedContents.forEach(item => {
                if (item.fieldName === 'question') {
                    transformed[item.languageCode] = item.translatedValue;
                }
            });
        }

        // ALWAYS set questionText - use current language first, then any available, then original
        if (!transformed.questionText) {
            if (i18n.language && transformed[i18n.language]) {
                transformed.questionText = transformed[i18n.language];
            } else if (question.localizedContents?.length > 0) {
                const firstQuestion = question.localizedContents.find(item => item.fieldName === 'question' && item.translatedValue);
                if (firstQuestion) {
                    transformed.questionText = firstQuestion.translatedValue;
                } else if (question.questionText) {
                    transformed.questionText = question.questionText;
                }
            } else if (question.questionText) {
                transformed.questionText = question.questionText;
            }
        }

        // Process subQuestions
        if (question.subQuestions?.length > 0) {
            transformed.subQuestions = question.subQuestions.map(subQn => {
                const transformedSub = { ...subQn };

                // Extract from localizedContents for sub-questions
                if (subQn.localizedContents?.length > 0) {
                    subQn.localizedContents.forEach(item => {
                        if (item.fieldName === 'question') {
                            transformedSub[item.languageCode] = item.translatedValue;
                        }
                    });
                }

                // ALWAYS set questionText for sub-questions
                if (!transformedSub.questionText) {
                    if (i18n.language && transformedSub[i18n.language]) {
                        transformedSub.questionText = transformedSub[i18n.language];
                    } else if (subQn.localizedContents?.length > 0) {
                        const firstSubQuestion = subQn.localizedContents.find(item => item.fieldName === 'question' && item.translatedValue);
                        if (firstSubQuestion) {
                            transformedSub.questionText = firstSubQuestion.translatedValue;
                        } else if (subQn.questionText) {
                            transformedSub.questionText = subQn.questionText;
                        }
                    } else if (subQn.questionText) {
                        transformedSub.questionText = subQn.questionText;
                    }
                }

                return transformedSub;
            });
        }

        return transformed;
    };

    return (
        <div className="qualitative-data-card-view">
            <div className="qualitative-data-card-view-content">
                {renderQualitativeContent()}
            </div>

            <Modal show={showCommentsModal} centered onHide={handleCommentsModalClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t("QualitativeDataCardView.modal.comments.title.label")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <div className="comments-section-box">
                            <div className="comments-section-header">
                                <Form.Label className="comments-section-label">{t("QualitativeDataCardView.modal.comments.label.label")}</Form.Label>
                                {!isCommentEdit && (
                                    <OverlayTrigger placement="top" overlay={<Tooltip>{t("Edit.label")}</Tooltip>}>
                                        <span
                                            className="comments-section-editBtn"
                                            role="button"
                                            onClick={onEditComments}
                                        >
                                            <BiPencil />
                                        </span>
                                    </OverlayTrigger>
                                )}
                            </div>
                            {isCommentEdit ? (
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    className="comments-section-textarea"
                                    value={tempComments}
                                    onChange={(e) => setTempComments(e.target.value)}
                                />
                            ) : (
                                qnComments
                                    ? <div className="comments-section-remark">{qnComments}</div>
                                    : <div className="comments-section-empty"><FcFinePrint className="me-2" />{t("QualitativeDataCardView.modal.noComments.label")}</div>
                            )}
                        </div>
                    </Form.Group>

                    {isCommentEdit && (
                        <Form.Group className="mb-3">
                            <div className="comments-section-fileUpload">
                                <Form.Label>{t("QualitativeDataCardView.modal.files.label.label")}</Form.Label>
                                <Form.Control
                                    type="file"
                                    multiple
                                    onChange={handleFileChange}
                                    ref={fileInputRef}
                                />
                            </div>
                        </Form.Group>
                    )}

                    <div className="attached-files-list">
                        <label className='mb-1'><b>{t("Documents&File.label")}</b></label>
                        {(isCommentEdit ? tempAttachments : attachedFiles)?.length > 0 ? (
                            <div className="attached-file-items">
                                {(isCommentEdit ? tempAttachments : attachedFiles).map((file, index) => (
                                    <div key={index} className="attached-file-item">
                                        <span className="attached-file-iconChip">
                                            <BsFileEarmarkFill className="attached-file-icon" />
                                        </span>
                                        <span
                                            className="attached-file-link"
                                            role="button"
                                            title={file.name}
                                            onClick={() => downloadAttchement(file)}
                                        >
                                            {file.name}
                                        </span>
                                        {isCommentEdit && (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="attached-file-removeBtn flex-shrink-0"
                                                onClick={() => showConfirm(file)}
                                            >
                                                <BsX />
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="attached-files-empty"><FcFinePrint className="me-2" />{t("QualitativeDataCardView.modal.noAttachments.label")}</div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    {isCommentEdit ? (
                        <>
                            <Button variant="secondary" onClick={() => setIsCommentEdit(false)}>
                                {t("Cancel.label")}
                            </Button>
                            <Button variant="primary" onClick={saveCommentsAndFiles}>
                                {t("Update.label")}
                            </Button>
                        </>
                    ) : (
                        <Button variant="secondary" onClick={handleCommentsModalClose}>
                            {t("Close.label")}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {showConfirmation && (
                <ConfirmationModal
                    modalShow={true}
                    messageText={messageConfirmation}
                    callbackModal={(confirmed) => {
                        setShowConfirmation(false);
                        if (confirmed) {
                            deleteFile();
                        }
                    }}
                />
            )}
        </div>
    );
};

export default withTranslation()(QualitativeDataCardView);